<script>
import {
  iAccordionItemSizes,
  iIcon,
  iTooltipAlignmentMode,
} from '@inscribe/signal';
import DocumentRightPanelEmptyState from '@/V2/documents/document-view/DocumentRightPanelEmptyState';
import InfoIcon from '@/V2/components/InfoIcon';
import LoadingBoxes from '@/V2/components/LoadingBoxes';
import SignalButton from '@/V2/documents/document-view/SignalButton';
import SignalTools from '@/V2/documents/document-view/signals/SignalTools';
import {
  getVerificationTitle,
  getGroupTotalCount,
} from '@/V2/documents/document-view/document-review-agent/utils';
import {
  ANALYST_GROUPS,
  VERIFICATION_RESULT,
  VERIFICATION_RESULT_SORT_ORDER,
  RISK_LEVEL,
} from '@/constants/analyst-constants';
import {
  ANALYST_GROUPS_CONFIG,
  RISKS_CONFIG,
} from '@/constants/analyst-config';

export default {
  components: {
    DocumentRightPanelEmptyState,
    InfoIcon,
    iIcon,
    LoadingBoxes,
    SignalButton,
    SignalTools,
  },
  props: {
    isLoading: {
      type: Boolean,
      default: false,
    },
    group: {
      type: Object,
      default: null,
    },
    compactVersion: {
      type: Boolean,
      default: false,
    },
    showLoadingState: {
      type: Boolean,
      default: true,
    },
    emptyStateTitle: {
      type: String,
      default: '',
    },
    emptyStateSubtitle: {
      type: String,
      default: '',
    },
    emptyStateLabel: {
      type: String,
      default: '',
    },
    emptyStateIcon: {
      type: String,
      default: 'medium_file_alert_1',
    },
  },
  data() {
    return {
      getVerificationTitle,
      iAccordionItemSizes,
      iTooltipAlignmentMode,
      ANALYST_GROUPS,
      ANALYST_GROUPS_CONFIG,
      RISKS_CONFIG,
      RISK_LEVEL,
      VERIFICATION_RESULT,
      verificationWithMenuOpen: null,
      getGroupTotalCount,
    };
  },
  computed: {
    serialisedVerifications() {
      let verifications = [];
      this.group?.riskLevels?.forEach((riskLevel) => {
        verifications = [...verifications, ...riskLevel.verifications];
      });
      return verifications;
    },
    visibleVerifications() {
      return this.compactVersion
        ? this.getHighestRankVerifications
        : this.serialisedVerifications;
    },
    getHighestRankVerifications() {
      if (this.serialisedVerifications.length) {
        const highestRank = this.serialisedVerifications[0].result;
        return this.serialisedVerifications.filter(
          (verification) => verification.result === highestRank,
        );
      } else {
        return [];
      }
    },
    groupSubtitle() {
      const count = this.getGroupTotalCount(this.group);
      return `${count} ${
        this.group.key === ANALYST_GROUPS.FRAUD
          ? count === 1
            ? 'signal detected'
            : 'signals detected'
          : count === 1
            ? 'result'
            : 'results'
      }`;
    },
    /**
     * this function loops through the serialised verifications and find ones
     * with lower level than highest
     */
    hasUnseenLevels() {
      let total = 0;
      const levelsMap = {};
      if (this.serialisedVerifications.length) {
        const highestRank = this.serialisedVerifications[0].result;
        this.serialisedVerifications.forEach((verification) => {
          if (
            VERIFICATION_RESULT_SORT_ORDER[verification.result] >
            VERIFICATION_RESULT_SORT_ORDER[highestRank]
          ) {
            total++;
            if (!levelsMap[verification.result]) {
              levelsMap[verification.result] = {
                ...RISKS_CONFIG[verification.result],
                total: 0,
              };
            }
            levelsMap[verification.result].total++;
          }
        });
      }
      return {
        levelsMap,
        total,
      };
    },
    viewAllLabels() {
      const labels = [];
      this.serialisedVerifications.forEach((verification) => {
        let found = labels.find((l) => l.key === verification.result);
        if (!found) {
          found = {
            key: verification.result,
            count: 0,
          };
          labels.push(found);
        }
        found.count++;
      });
      return labels.map((level) => {
        return this.getLevelLabel(level);
      });
    },
  },
  methods: {
    getLevelLabel(level) {
      return `${level.count} ${RISKS_CONFIG[level.key].label.replace(
        ' Risk',
        '',
      )}`;
    },
    onButtonClicked(verification) {
      this.$emit('show-item-details', verification);
    },
    viewAll() {
      this.$emit('view-all', this.group);
    },
    shouldShowRiskLabelBefore(verification) {
      let isFirstRiskVerification = false;
      this.group?.riskLevels?.forEach((riskLevel) => {
        if (riskLevel.verifications[0] === verification) {
          isFirstRiskVerification = true;
        }
      });
      return !this.compactVersion && isFirstRiskVerification;
    },
    getRiskCount(riskKey) {
      const riskLevel = this.group?.riskLevels?.find(
        (risk) => risk.key === riskKey,
      );
      return riskLevel?.verifications?.length || 0;
    },
    onEmptyStateClick(evt) {
      this.$emit('empty-state-btn-click', evt);
    },
  },
};
</script>

<template>
  <div
    :class="{
      'i-px-6': !compactVersion,
      'i-py-10': !compactVersion,
      extended: !compactVersion,
    }"
    class="document-right-data-box"
    data-testid="document-right-data-box"
  >
    <!-- loading screen -->
    <loading-boxes
      v-if="isLoading && compactVersion && showLoadingState"
      class="accordion-content i-mx-6"
    ></loading-boxes>

    <!-- empty state -->
    <document-right-panel-empty-state
      v-else-if="!showLoadingState && serialisedVerifications.length === 0"
      :symbol="emptyStateIcon"
      :title="emptyStateTitle"
      :sub-title="emptyStateSubtitle"
      :button-text="emptyStateLabel || null"
      class="i-mx-6 i-mb-10"
      data-testid="document-right-data-box-empty-state"
      @on-button-click="onEmptyStateClick"
    ></document-right-panel-empty-state>

    <!-- content -->
    <div v-else class="items-list accordion-content d-flex flex-column">
      <!-- full list title -->
      <div
        v-if="!compactVersion"
        class="d-flex flex-column flex-grow-1 i-mx-6 i-mb-2"
      >
        <div class="title d-flex i-text-button-2 i-text-neutral-120">
          {{ ANALYST_GROUPS_CONFIG[group?.key]?.label }}
        </div>
        <div class="subtitle d-flex i-text-body-3 i-text-neutral-110">
          {{ groupSubtitle }}
        </div>
      </div>

      <div class="signals-list d-flex flex-column i-gap-1">
        <!-- full list -->
        <div
          v-for="(verification, verifications_index) in visibleVerifications"
          :key="verifications_index"
        >
          <div
            v-if="shouldShowRiskLabelBefore(verification)"
            class="verification-risk-label d-flex align-items-center i-px-8 i-py-4 i-mx-6 i-mt-8 i-mb-4 i-radius-md"
          >
            <div class="label d-flex i-gap-5">
              <div class="i-text-neutral-120 i-text-subheading-3">
                {{ RISKS_CONFIG[verification.result].label }}
              </div>
              <div class="i-text-neutral-90 i-text-body-3">
                {{ getRiskCount(verification.result) }}
              </div>
            </div>
            <info-icon
              v-if="RISKS_CONFIG[verification.result].tooltipInfo"
              :title="RISKS_CONFIG[verification.result].label"
              :description="RISKS_CONFIG[verification.result].tooltipInfo"
              :alignment="iTooltipAlignmentMode.TOP_RIGHT"
            />
          </div>
          <signal-button
            :title="verification.title"
            :subtitle="verification.subtitle"
            :success="
              [VERIFICATION_RESULT.LOW_RISK, RISK_LEVEL.TRUST].includes(
                verification.result,
              ) || verification.passed === true
            "
            :warning="verification.result === VERIFICATION_RESULT.INCONCLUSIVE"
            :has-menu-open="
              verificationWithMenuOpen?.title === verification.title
            "
            @button-click="onButtonClicked(verification)"
          >
            <template #tools>
              <signal-tools
                v-if="verification.detailsType === 'fraud'"
                :signal-group="verification"
                @i-menu-open-changed="
                  (evt) => {
                    verificationWithMenuOpen = evt ? verification : null;
                  }
                "
              ></signal-tools>
            </template>
          </signal-button>
        </div>
      </div>
      <!-- view all button -->
      <button
        v-if="compactVersion && hasUnseenLevels.total > 0"
        class="view-all-button d-flex align-items-center i-radius-md i-px-8 i-py-4 i-mt-8 i-mx-6"
        data-testid="view-all-button"
        @click="viewAll"
      >
        <div class="i-text-button-3 i-text-neutral-120">View All</div>
        <div class="d-flex align-items-center i-gap-8">
          <div class="all-labels d-flex align-items-center i-gap-4">
            <div
              v-for="(riskLabel, labelIndex) in viewAllLabels"
              :key="labelIndex"
              class="i-text-body-3 i-text-neutral-110"
            >
              {{ riskLabel }}
            </div>
          </div>
          <i-icon
            symbol="medium-chevron-right"
            class="i-text-neutral-90"
            :size="16"
          ></i-icon>
        </div>
      </button>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.document-right-data-box {
  .verification-risk-label {
    background: $light-neutral-20;
    justify-content: space-between;
  }

  .view-all-button {
    height: 36px;
    border: 0;
    background: var(--surface-neutral-subtle);
    transition: background 150ms ease-in-out;
    justify-content: space-between;
    cursor: pointer;

    &:hover {
      background: $light-neutral-30;
    }
  }

  :deep(.signal-button) {
    .subtitle {
      overflow: hidden;
      white-space: nowrap;
      max-width: 100%;
      text-overflow: ellipsis;
    }
  }
}
</style>
